import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAdminList, API, authHeaders } from "./adminApi";
import ImagePicker, { resolveImageUrl } from "./ImagePicker";
import { REALISATION_CATEGORIES } from "../../components/Realisations";

const EMPTY = { title: "", category: "jardins", before_url: "", after_url: "", location: "", visible: true };

export default function RealisationsAdmin({ token, onUnauthorized }) {
  const { data, loading, setData } = useAdminList(token, "/admin/realisations", onUnauthorized);
  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState(false);
  const edit = (item) => {
    setDraft(item);
    setOpen(true);
  };

  const save = async () => {
    const { id, order, ...payload } = draft;
    try {
      const res = id
        ? await axios.put(`${API}/admin/realisations/${id}`, payload, { headers: authHeaders(token) })
        : await axios.post(`${API}/admin/realisations`, payload, { headers: authHeaders(token) });
      setData((d) => (id ? d.map((x) => (x.id === id ? res.data : x)) : [res.data, ...d]));
      toast.success(id ? "Réalisation modifiée" : "Réalisation ajoutée");
      setOpen(false);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    try {
      await axios.delete(`${API}/admin/realisations/${id}`, { headers: authHeaders(token) });
      setData((d) => d.filter((x) => x.id !== id));
      toast.success("Réalisation supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const valid = draft && draft.title && draft.before_url && draft.after_url;

  return (
    <div data-testid="admin-realisations-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">Réalisations avant / après</h2>
          <p className="text-[#5C705E] mt-1">Section « Nos réalisations » de l'accueil (masquée tant qu'elle est vide).</p>
        </div>
        <Button onClick={() => edit({ ...EMPTY })} data-testid="realisation-create" className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white">
          <Plus className="w-4 h-4 mr-2" /> Ajouter une réalisation
        </Button>
      </header>

      {loading ? (
        <div className="text-center py-20 text-[#5C705E]">Chargement…</div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-[#5C705E] bg-white rounded-2xl border border-dashed border-[#E5EBE5]">
          Aucune réalisation. Ajoutez une photo « avant » et une photo « après ».
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="realisations-admin-list">
          {data.map((item) => (
            <div key={item.id} data-testid={`realisation-admin-${item.id}`} className="bg-white border border-[#E5EBE5] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2 gap-0.5 bg-[#E5EBE5]">
                <img src={resolveImageUrl(item.before_url)} alt="Avant" className="aspect-[4/3] w-full object-cover" />
                <img src={resolveImageUrl(item.after_url)} alt="Après" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="p-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold">
                    {REALISATION_CATEGORIES.find((c) => c.key === item.category)?.label}
                    {!item.visible && <EyeOff className="inline w-3.5 h-3.5 ml-2 text-[#7B5300]" />}
                  </div>
                  <div className="font-['Outfit'] font-semibold text-[#1A2E1C] truncate">{item.title}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit({ ...item })} data-testid={`realisation-edit-${item.id}`} className="p-2 rounded-full hover:bg-[#E8F5E9]">
                    <Pencil className="w-4 h-4 text-[#1B5E20]" />
                  </button>
                  <button onClick={() => remove(item.id)} data-testid={`realisation-delete-${item.id}`} className="p-2 rounded-full hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-[#D32F2F]/80" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby="realisation-form-desc" className="max-w-2xl rounded-3xl p-6 border-[#E5EBE5] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C]">
              {draft?.id ? "Modifier la réalisation" : "Nouvelle réalisation"}
            </DialogTitle>
            <p id="realisation-form-desc" className="text-sm text-[#5C705E]">Photos de même cadrage conseillées (format paysage).</p>
          </DialogHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="mb-1.5 block">Titre</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Création d'un massif fleuri" data-testid="realisation-title-input" className="rounded-xl h-11" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Catégorie</Label>
                  <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="realisation-category-select" className="w-full h-11 rounded-xl border border-[#E5EBE5] px-3 bg-white">
                    {REALISATION_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Lieu (facultatif)</Label>
                  <Input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Guéret" data-testid="realisation-location-input" className="rounded-xl h-11" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <ImagePicker token={token} label="Photo AVANT" value={draft.before_url} onChange={(url) => setDraft((d) => ({ ...d, before_url: url }))} />
                <ImagePicker token={token} label="Photo APRÈS" value={draft.after_url} onChange={(url) => setDraft((d) => ({ ...d, after_url: url }))} />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={draft.visible} onChange={(e) => setDraft({ ...draft, visible: e.target.checked })} className="w-5 h-5" />
                <span className="text-sm">Visible sur le site</span>
              </label>
            </div>
          )}
          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={save} disabled={!valid} data-testid="realisation-save" className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
